import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useRef } from "react";

/**
 * Member-side confirmation dialog. Radix supplies the focus trap, Escape to
 * close, `aria-labelledby` / `aria-describedby` wiring, and returns focus to
 * the element that opened it. Styled with page CSS rather than the shadcn
 * `ui/dialog`, because Tailwind is scoped to the admin routes (admin.css).
 *
 * Cancel is the first focusable control, so it takes initial focus and Enter
 * never confirms a delete by accident.
 */
function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const returnTo = useRef(null);
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel?.();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="ch-dialog__overlay" />
        <DialogPrimitive.Content
          className="ch-dialog"
          onOpenAutoFocus={() => {
            returnTo.current = document.activeElement;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = returnTo.current;
            returnTo.current = null;
            if (target && target.isConnected && typeof target.focus === "function") {
              target.focus();
            }
          }}
        >
          <DialogPrimitive.Title className="ch-dialog__title">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="ch-dialog__body">
            {description}
          </DialogPrimitive.Description>
          <div className="ch-dialog__actions">
            <button
              type="button"
              className="ch-btn ch-btn--secondary"
              onClick={onCancel}
              disabled={busy}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={`ch-btn ${destructive ? "ch-btn--danger" : "ch-btn--primary"}`}
              onClick={onConfirm}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              {busy ? "Working…" : confirmLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default ConfirmDialog;
